/**
 * LightsPanel Component
 * 
 * Panel untuk manage custom lights:
 * - Add lights (Point, Spot, Directional, Area)
 * - Edit light properties (color, intensity, position, shadows)
 * - Delete lights
 * - Toggle helpers
 */

import React from 'react'

export default function LightsPanel({
  isOpen,
  onClose,
  viewportReady,
  isMobile,
  customLights,
  selectedLight,
  setSelectedLight,
  addCustomLight,
  updateCustomLight,
  removeCustomLight,
}) {
  if (!isOpen || !viewportReady) return null

  return (
    <div
      style={{
        position: 'absolute',
        top: '20px',
        left: '88px',
        zIndex: 18,
        width: isMobile ? 'calc(100% - 120px)' : '340px',
        maxHeight: 'calc(100vh - 100px)',
        background: 'linear-gradient(135deg, rgba(20, 20, 30, 0.72) 0%, rgba(10, 10, 15, 0.78) 100%)',
        backdropFilter: 'blur(24px) saturate(190%)',
        WebkitBackdropFilter: 'blur(24px) saturate(190%)',
        borderRadius: '16px',
        padding: '20px',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15), 0 16px 48px rgba(0, 0, 0, 0.12)',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        border: '0.5px solid rgba(255, 255, 255, 0.18)',
        borderTop: '0.5px solid rgba(255, 255, 255, 0.25)',
        color: '#fff',
        animation: 'slideInFromLeft 0.5s cubic-bezier(0.32, 0.72, 0, 1) 0.1s forwards',
        opacity: 0,
        userSelect: 'none',
        overflowY: 'auto',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
        }}
      >
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>
          💡 Custom Lights ({customLights.length})
        </h3>
        <button
          onClick={onClose}
          style={{
            padding: '6px 10px',
            backgroundColor: 'rgba(239, 68, 68, 0.2)',
            color: '#ef4444',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '12px',
          }}
        >
          ✕
        </button>
      </div>

      {/* Add Light Buttons */}
      <div
        style={{
          display: 'flex',
          gap: '6px',
          marginBottom: '16px',
          flexWrap: 'wrap',
        }}
      >
        <button
          onClick={() => addCustomLight('point')}
          style={{
            padding: '8px 12px',
            backgroundColor: '#374151',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '11px',
            fontWeight: 500,
          }}
        >
          ⚫ Point
        </button>
        <button
          onClick={() => addCustomLight('spot')}
          style={{
            padding: '8px 12px',
            backgroundColor: '#374151',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '11px',
            fontWeight: 500,
          }}
        >
          🔦 Spot
        </button>
        <button
          onClick={() => addCustomLight('directional')}
          style={{
            padding: '8px 12px',
            backgroundColor: '#374151',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '11px',
            fontWeight: 500,
          }}
        >
          ☀️ Directional
        </button>
        <button
          onClick={() => addCustomLight('rect')}
          style={{
            padding: '8px 12px',
            backgroundColor: '#374151',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '11px',
            fontWeight: 500,
          }}
        >
          ▭ Area
        </button>
      </div>

      {/* Lights List */}
      {customLights.length === 0 ? (
        <div
          style={{
            padding: '40px 20px',
            textAlign: 'center',
            color: 'rgba(255, 255, 255, 0.4)',
            fontSize: '12px',
          }}
        >
          <div style={{ fontSize: '32px', marginBottom: '8px' }}>💡</div>
          <div>No custom lights</div>
          <div style={{ fontSize: '10px', marginTop: '4px', opacity: 0.7 }}>
            Add lights using buttons above
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {customLights.map((light) => {
            const isSelected = selectedLight?.id === light.id
            return (
              <div
                key={light.id}
                style={{
                  backgroundColor: isSelected ? '#2d3748' : '#1e1e23',
                  borderRadius: '8px',
                  border: isSelected ? '1px solid rgba(59, 130, 246, 0.5)' : '1px solid rgba(255, 255, 255, 0.1)',
                  overflow: 'hidden',
                }}
              >
                {/* Light Header */}
                <div
                  onClick={() => setSelectedLight(isSelected ? null : light)}
                  style={{
                    padding: '12px',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '16px' }}>
                      {light.type === 'point' ? '⚫' : light.type === 'spot' ? '🔦' : light.type === 'directional' ? '☀️' : '▭'}
                    </span>
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: 500 }}>
                        {light.name}
                      </div>
                      <div style={{ fontSize: '10px', opacity: 0.6 }}>
                        {light.type.charAt(0).toUpperCase() + light.type.slice(1)}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      removeCustomLight(light.id)
                    }}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: 'rgba(239, 68, 68, 0.2)',
                      color: '#ef4444',
                      border: '1px solid rgba(239, 68, 68, 0.3)',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '10px',
                    }}
                  >
                    ✕
                  </button>
                </div>

                {/* Light Controls (expanded when selected) */}
                {isSelected && (
                  <div style={{ padding: '0 12px 12px 12px' }}>
                    {/* Color */}
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ fontSize: '11px', opacity: 0.6, display: 'block', marginBottom: '6px' }}>
                        Color
                      </label>
                      <input
                        type="color"
                        value={light.color}
                        onChange={(e) => updateCustomLight(light.id, 'color', e.target.value)}
                        style={{
                          width: '100%',
                          height: '36px',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: '6px',
                          cursor: 'pointer',
                        }}
                      />
                    </div>

                    {/* Intensity */}
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ fontSize: '11px', opacity: 0.6, display: 'block', marginBottom: '4px' }}>
                        Intensity: {light.intensity?.toFixed(2) || '1.00'}
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="10"
                        step="0.1"
                        value={light.intensity || 1}
                        onChange={(e) => updateCustomLight(light.id, 'intensity', parseFloat(e.target.value))}
                        style={{ width: '100%', accentColor: '#3b82f6' }}
                      />
                    </div>

                    {/* Position */}
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ fontSize: '11px', opacity: 0.6, display: 'block', marginBottom: '6px' }}>
                        Position
                      </label>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px' }}>
                        {['x', 'y', 'z'].map((axis) => (
                          <div key={axis}>
                            <label style={{ fontSize: '9px', opacity: 0.5, display: 'block', marginBottom: '2px' }}>
                              {axis.toUpperCase()}
                            </label>
                            <input
                              type="number"
                              step="0.1"
                              value={light.position?.[axis] || 0}
                              onChange={(e) =>
                                updateCustomLight(light.id, 'position', {
                                  ...light.position,
                                  [axis]: parseFloat(e.target.value) || 0,
                                })
                              }
                              style={{
                                width: '100%',
                                padding: '4px',
                                backgroundColor: '#0f0f12',
                                color: '#fff',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                borderRadius: '4px',
                                fontSize: '10px',
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Shadows Toggle */}
                    {(light.type === 'directional' || light.type === 'spot') && (
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <label style={{ fontSize: '11px', opacity: 0.6 }}>Cast Shadows</label>
                          <button
                            onClick={() => updateCustomLight(light.id, 'castShadow', !light.castShadow)}
                            style={{
                              padding: '4px 12px',
                              backgroundColor: light.castShadow ? '#3b82f6' : 'rgba(255, 255, 255, 0.1)',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '10px',
                              fontWeight: 600,
                            }}
                          >
                            {light.castShadow ? 'ON' : 'OFF'}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Helper Toggle */}
                    <div style={{ marginBottom: '0' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <label style={{ fontSize: '11px', opacity: 0.6 }}>Show Helper</label>
                        <button
                          onClick={() => updateCustomLight(light.id, 'showHelper', !light.showHelper)}
                          style={{
                            padding: '4px 12px',
                            backgroundColor: light.showHelper ? '#10b981' : 'rgba(255, 255, 255, 0.1)',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '10px',
                            fontWeight: 600,
                          }}
                        >
                          {light.showHelper ? 'ON' : 'OFF'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

